import React, { useEffect, useState } from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Autocomplete, Stack, TextField, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useUrlHooks } from "hooks";
import useDeals from "hooks/tagger-hooks/useDeals";
import { useInvestorsSelector } from "store";
import { InvestorWithBank } from "store/types";

import { fundFilesTypes } from "../../../constants";
import { SelectAutocomplete } from "../../forms";
import { GeneralModal } from "../General-modal/GeneralModal";

import "./TagNewPdfModal.styles.scss";

type AutocompleteOption = { label: string; value: number };

export type TagNewPdfModalProps = {
  open: boolean;
  onClose: () => void;
  onNext: (deal: { value: number; label: string }, documentType?: string | null, investorId?: number) => void;
};

export const TagNewPdfModal: React.FC<TagNewPdfModalProps> = (props) => {
  const {
    queryParams: { dealId }
  } = useUrlHooks();
  const investors = useInvestorsSelector();

  const { options, value: stateDealId, loading: dealsLoading } = useDeals();
  const [selectedDeal, setSelectedDeal] = useState<{ label: string; value: number } | null>(null);
  const [documentType, setDocumentType] = useState<string | null>(null);
  const [investor, setInvestor] = useState<InvestorWithBank | null>(null);

  useEffect(() => {
    if (dealId) {
      const foundDeal = options.find((opt) => opt.value === Number(dealId));
      if (foundDeal) {
        setSelectedDeal(foundDeal);
      }
    }
  }, [dealId, options]);

  return (
    <GeneralModal
      open={props.open}
      onClose={props.onClose}
      title="Tag New PDF File"
      positiveBtn={{
        text: "Next",
        disabled: !selectedDeal || (Boolean(investor) && !documentType),
        onClick: () => {
          props.onNext(selectedDeal!, documentType || null, investor?.id);
          props.onClose();
        }
      }}
      negativeBtn={{ text: "Cancel", onClick: props.onClose }}
    >
      <div className="tag-new-pdf-modal__content">
        <Typography className="tag-new-pdf-modal__title" variant="body1">
          Select Deal and folder to place this file
        </Typography>
        <SelectAutocomplete<AutocompleteOption>
          size="small"
          fullWidth
          loading={dealsLoading}
          value={selectedDeal}
          setValue={(newValue) => setSelectedDeal(newValue || null)}
          options={options}
          disableClearable
          disabled={selectedDeal?.value === stateDealId}
          compareParam={(opt) => opt.label}
          getOptionLabel={(option) => option.label}
          renderOption={(props, option) => (
            <li {...props} key={option.value}>
              {option.label}
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} fullWidth label="Deal" InputLabelProps={{ shrink: true }} variant="outlined" />
          )}
        />
        <Stack direction="row" gap="16px" alignItems="center" position="relative">
          <Autocomplete<string>
            sx={{ width: "100%" }}
            options={fundFilesTypes}
            value={documentType || null}
            onChange={(_, option: string | null) => {
              setDocumentType(option || null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label="File Type"
                InputLabelProps={{ shrink: true }}
                variant="outlined"
              />
            )}
          />

          {documentType && (
            <MaterialTooltip title="Folder is predefined according to document type" isShow>
              <InfoOutlinedIcon
                sx={{
                  position: "absolute",
                  right: -23,
                  top: 10,
                  fontSize: "16px",
                  cursor: "help",
                  color: "#496682",
                  width: "16px",
                  minWidth: "16px",
                  flexShrink: 0
                }}
              />
            </MaterialTooltip>
          )}
          {Boolean(!documentType && investor) && (
            <MaterialTooltip
              isShow={Boolean(!documentType && investor)}
              title="File type is required when an investor is selected"
            >
              <InfoOutlinedIcon
                sx={{
                  position: "absolute",
                  right: -23,
                  top: 10,
                  fontSize: "16px",
                  cursor: "help",
                  color: "#496682",
                  width: "16px",
                  minWidth: "16px",
                  flexShrink: 0
                }}
              />
            </MaterialTooltip>
          )}
        </Stack>
        <Autocomplete
          options={investors}
          value={investor}
          getOptionLabel={(option) => option.name}
          renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: InvestorWithBank) => (
            <li {...props} key={option.id}>
              {option.name}
            </li>
          )}
          onChange={(_, option) => setInvestor(option || null)}
          renderInput={(params) => <TextField {...params} label="Investor" />}
        />
      </div>
    </GeneralModal>
  );
};
